// features/council/PublicRecordPage.tsx — the Council's record, readable
// without an account.
//
// The front page makes this promise twice, in these words: "see the Council
// ... no account needed" and "The real record -- every case, every vote,
// every written reason -- is public at the Council, no account needed."
// Until 1.24.80 both links put a signed-out reader on a login form, which is
// the one thing a platform whose pitch is "moderation you can see" cannot do.
// A skeptic who follows the proof link and is asked to join has been told the
// opposite of what the page said.
//
// This is the read-only half of /council. It shows what the server already
// serves an anonymous caller (apps/moderation.PublicCaseSerializer): the
// cases that have OPENED, never the ones still gathering reports -- a case
// below the threshold is an accusation nobody has weighed, and publishing
// those would make a single report a public smear, which is the exact thing
// the accumulation gate exists to stop. No reporter is ever named. A live
// tally is withheld until the decision lands, then published with it.
//
// Members keep the full console (CouncilLayout); voting has never been open to
// a visitor and is not opened here. Reading is.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Panel } from '@/ui';
import { Icon } from '@/icons';
import { useCases, usePageTitle } from '@/hooks';
import { casesService } from '@/core/services/casesService';
import type { CaseRecord } from '@/core/models/case';
import { CategoryBadge } from './components/CategoryBadge';
import { StatusPill } from './components/StatusPill';
import { resolvedCases } from './filterCases';
import { useCaseTarget } from './useCaseTarget';

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.color.bgRoot};
  padding: 32px 24px 64px;
`;

const Inner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.color.textPrimary};
  }
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.display};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const Lead = styled.p`
  margin: 0;
  max-width: 68ch;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.65;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Card = styled(Panel)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Entry = styled.article`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 0;
  & + & {
    border-top: 1px solid ${({ theme }) => theme.color.glassBorder};
  }
`;

const Head = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const HeadLink = styled(Link)`
  color: ${({ theme }) => theme.color.textPrimary};
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const Time = styled.span`
  margin-left: auto;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Outcome = styled.p<{ $removed: boolean }>`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme, $removed }) => ($removed ? theme.color.danger : theme.color.success)};
`;

const Reasons = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.6;
  color: ${({ theme }) => theme.color.textMuted};
`;

const Tally = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.mono};
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Pending = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

const Empty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
`;

const Foot = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
  a {
    color: ${({ theme }) => theme.color.accentCyan};
  }
`;

/** "Kept, 6-1" / "Removed, 7-0" — the shape the front page advertises. */
export function tallyLine(c: CaseRecord): string | null {
  if (!c.decision) return null;
  const { keep, remove } = c.votes;
  if (keep + remove === 0) return null;
  const word = c.decision.outcome === 'removed' ? 'Removed' : 'Kept';
  const [winner, loser] = c.decision.outcome === 'removed' ? [remove, keep] : [keep, remove];
  return `${word}, ${winner}-${loser}`;
}

function RecordEntry({ c }: { c: CaseRecord }) {
  const t = useCaseTarget(c);
  const tally = tallyLine(c);
  return (
    <Entry>
      <Head>
        <CategoryBadge category={c.category} />
        <StatusPill status={c.status} />
        {t.href ? <HeadLink to={t.href}>{t.headline}</HeadLink> : <span>{t.headline}</span>}
        <Time>{c.time}</Time>
      </Head>
      {c.decision ? (
        <>
          <Outcome $removed={c.decision.outcome === 'removed'}>
            {c.decision.outcome === 'removed' ? 'Removed' : 'Kept visible'}
          </Outcome>
          <Reasons>{c.decision.explanation}</Reasons>
          {c.appeal && <Reasons>{c.appeal.explanation}</Reasons>}
          {tally && <Tally>{tally}</Tally>}
        </>
      ) : (
        <Pending>
          Open. The post stays fully visible while a team decides, and the decision and its reasons
          appear here when it lands.
        </Pending>
      )}
    </Entry>
  );
}

export function PublicRecordPage() {
  usePageTitle('The Council / TruXSocial');
  const { cases } = useCases();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    // The list endpoint is AllowAny and serves an anonymous caller the public
    // projection; a failure leaves the record empty rather than erroring the
    // page, and the empty state below reads correctly either way.
    casesService
      .listCases()
      .catch(() => undefined)
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const decided = resolvedCases(cases);
  const open = cases.filter((c) => !resolvedCases([c]).length);

  return (
    <Page>
      <Inner>
        <Back to="/">
          <Icon name="arrow-left" size={16} /> TruXSocial
        </Back>
        <Title>The Council&apos;s record</Title>
        <Lead>
          Every decision the Council has made, with the reasons it gave and how the team voted. You
          do not need an account to read it. That is the point of publishing it. Reports themselves
          are never published: who reported whom stays private, and a report that has not met the
          threshold is an accusation nobody has weighed yet, so it does not appear here at all.
        </Lead>

        <Card>
          {!loaded && <Pending>Loading the record…</Pending>}

          {loaded && decided.length === 0 && open.length === 0 && (
            <Empty>
              <Pending>
                No case has been decided yet. TruXSocial is small and new, and a report only becomes
                a case once enough independent reports agree there is something to decide, so an
                empty record here means nothing has crossed that line, not that decisions are being
                kept from you.
              </Pending>
              <Pending>
                When one is decided, it appears here in full: the outcome, the written reasons, and
                the vote.
              </Pending>
            </Empty>
          )}

          {loaded && decided.length === 0 && open.length > 0 && (
            <Pending>
              Nothing has been decided yet. {open.length}{' '}
              {open.length === 1 ? 'case is' : 'cases are'} open below. The posts stay fully visible
              while their teams decide.
            </Pending>
          )}

          {decided.map((c) => (
            <RecordEntry key={c.id} c={c} />
          ))}
          {open.map((c) => (
            <RecordEntry key={c.id} c={c} />
          ))}
        </Card>

        <Foot>
          The five rules the Council may act on are in the{' '}
          <Link to="/policies/guidelines">Community Guidelines</Link>, and how a case is decided is
          in the <Link to="/policies/moderation">Moderation Policy</Link>. Voting is done by members
          drawn at random. <Link to="/signin?next=%2Fcouncil">Sign in</Link> to see the full
          console.
        </Foot>
      </Inner>
    </Page>
  );
}
